/** Geodesy helpers. Distances in metres, angles in degrees unless noted. */

export const EARTH_RADIUS_M = 6_371_008.8;
const RAD = Math.PI / 180;

export interface LatLon {
  lat: number;
  lon: number;
}

export function haversine(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const p1 = aLat * RAD;
  const p2 = bLat * RAD;
  const dp = p2 - p1;
  const dl = (bLon - aLon) * RAD;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function distance(a: LatLon, b: LatLon): number {
  return haversine(a.lat, a.lon, b.lat, b.lon);
}

/** Local equirectangular projection around an origin; good to <0.1% within ~50 km. */
export class LocalProjection {
  readonly mPerDegLat = EARTH_RADIUS_M * RAD;
  readonly mPerDegLon: number;
  constructor(readonly origin: LatLon) {
    this.mPerDegLon = this.mPerDegLat * Math.cos(origin.lat * RAD);
  }
  toXY(p: LatLon): { x: number; y: number } {
    return {
      x: (p.lon - this.origin.lon) * this.mPerDegLon,
      y: (p.lat - this.origin.lat) * this.mPerDegLat,
    };
  }
  toLatLon(x: number, y: number): LatLon {
    return {
      lat: this.origin.lat + y / this.mPerDegLat,
      lon: this.origin.lon + x / this.mPerDegLon,
    };
  }
}

/** Initial bearing a→b in degrees [0, 360). */
export function bearing(a: LatLon, b: LatLon): number {
  const p1 = a.lat * RAD;
  const p2 = b.lat * RAD;
  const dl = (b.lon - a.lon) * RAD;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) / RAD + 360) % 360;
}

export interface BBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Longitude normalised to [-180, 180). */
export function normLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

/**
 * `lon` shifted by a multiple of 360 so it lies within 180° of `ref`. Antimeridian convention:
 * everything in one plan lives in ONE continuous longitude frame (the first point's), so a
 * bbox or route may reach beyond ±180 (e.g. 180.3 for −179.7); data sources wrap internally.
 */
export function unwrapLon(lon: number, ref: number): number {
  return ref + normLon(lon - ref);
}

/**
 * Bbox around points, padded by `padM` metres. Points are unwrapped relative to the first one, so
 * a set straddling ±180° gives a narrow box with west < -180 or east > 180, never a world-wide one.
 */
export function bboxAround(points: LatLon[], padM: number): BBox {
  let s = Infinity;
  let w = Infinity;
  let n = -Infinity;
  let e = -Infinity;
  const ref = points[0]?.lon ?? 0;
  for (const p of points) {
    const lon = unwrapLon(p.lon, ref);
    s = Math.min(s, p.lat);
    n = Math.max(n, p.lat);
    w = Math.min(w, lon);
    e = Math.max(e, lon);
  }
  const midLat = (s + n) / 2;
  const dLat = padM / (EARTH_RADIUS_M * RAD);
  const dLon = dLat / Math.max(0.01, Math.cos(midLat * RAD));
  return {
    south: Math.max(-90, s - dLat),
    north: Math.min(90, n + dLat),
    west: w - dLon,
    east: e + dLon,
  };
}
